import {
  ArtifactSchema,
  TraceFileSchema,
  TraceMessageSchema,
  TraceSchema,
} from '@terra/shared-schemas-ai';
import {
  BlockedReasonSchema,
  ConfirmedVulnerabilityFindingStateSchema,
  SignalCategorySchema,
  SignalRankSchema,
  SignalStageSchema,
  SignalStatusSchema,
} from '@terra/shared-schemas-orchestrator';
import { AssetLifecycleDtoSchema } from '@terra/shared-dto-types';
import { z } from 'zod';
import { BaseTraceDtoSchema } from './trace-base.schema';
import { TraceResultKeySchema } from './trace-sort.schema';

export const TraceWithMessagesAndFilesSchema = TraceSchema.extend({
  messages: z.array(TraceMessageSchema),
  files: z.array(TraceFileSchema),
});

export const TraceWithMessagesFilesAndArtifactsSchema = TraceSchema.extend({
  messages: z.array(TraceMessageSchema),
  files: z.array(TraceFileSchema),
  artifacts: z.array(ArtifactSchema),
});

export const TraceReturnSchema = BaseTraceDtoSchema;

// Inlined to avoid a circular dependency with `@terra/shared-contracts-signals`
// (which already depends on this contract for `TracePreviewBaseSchema`).
export const SignalPreviewSchema = z.object({
  id: z.string(),
  description: z.string().nullable(),
  category: SignalCategorySchema,
  confidence: z.int().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  applicationId: z.string(),
  endpointId: z.string().nullable(),
  entryPointId: z.string().nullable(),
  securityTestCaseId: z.string().nullable(),
  harEntryId: z.string().nullable(),
  rank: SignalRankSchema,
  stage: SignalStageSchema,
  status: SignalStatusSchema.nullable(),
  blockedReason: BlockedReasonSchema.nullable().optional(),
  confirmedVulnerabilityFindingState:
    ConfirmedVulnerabilityFindingStateSchema.nullable().optional(),
  isActive: z.boolean(),
  isAuthenticated: z.boolean().nullable(),
  lifecycle: AssetLifecycleDtoSchema.optional(),
});

const SKIP_ATTACK_REASON_TYPE_VALUES = [
  'authentication_failure',
  'signal_blocked_by_guardrails',
  'resume_max_retries_exceeded',
  'provider_content_filter',
] as const;

const SkipAttackReasonTypeEnumSchema = z.enum(SKIP_ATTACK_REASON_TYPE_VALUES);

export type SkipAttackReasonType = z.infer<
  typeof SkipAttackReasonTypeEnumSchema
>;

const LEGACY_SKIP_ATTACK_REASON_TYPE_MAP = {
  lead_blocked_by_guardrails: 'signal_blocked_by_guardrails',
} as const satisfies Record<string, SkipAttackReasonType>;

function remapLegacySkipAttackReasonType(value: unknown): unknown {
  if (typeof value !== 'string' || value.length === 0) {
    return undefined;
  }

  return Object.hasOwn(LEGACY_SKIP_ATTACK_REASON_TYPE_MAP, value)
    ? LEGACY_SKIP_ATTACK_REASON_TYPE_MAP[
        value as keyof typeof LEGACY_SKIP_ATTACK_REASON_TYPE_MAP
      ]
    : value;
}

export const SkipAttackReasonTypeSchema = z
  .preprocess(remapLegacySkipAttackReasonType, SkipAttackReasonTypeEnumSchema)
  .optional()
  .catch(undefined);

export function normalizeSkipAttackReasonType(
  value: string | null | undefined,
): SkipAttackReasonType | undefined {
  return SkipAttackReasonTypeSchema.parse(value);
}

// Base trace preview without signal info - used when caller already has signal data
export const TracePreviewBaseSchema = BaseTraceDtoSchema.omit({
  metadata: true,
});

// Extended trace preview with signal info - used when listing traces for an application
export const TracePreviewSchema = TracePreviewBaseSchema.extend({
  signal: z.union([SignalPreviewSchema, z.null()]),
  executionObjective: z.string().optional(),
  isArchived: z.boolean().optional(),
  skipAttackReasonType: SkipAttackReasonTypeSchema,
  skipAttackReason: z.string().optional(),
  resultKey: TraceResultKeySchema.optional(),
});

// Traces Trend - Query schema for filtering
export const TracesTrendQuerySchema = z.object({
  from: z
    .string()
    .optional()
    .describe('Start date for the trend period (ISO 8601 format)'),
  to: z
    .string()
    .optional()
    .describe('End date for the trend period (ISO 8601 format)'),
  applicationIds: z
    .string()
    .optional()
    .describe('Comma-separated list of application IDs to filter by'),
});

// Traces Trend - Response data schema
export const TracesTrendDataSchema = z.object({
  date: z.string().describe('Date in YYYY-MM-DD format'),
  traces: z.number().describe('Count of traces on this date'),
});

export const TracesTrendResponseSchema = z.object({
  data: z.array(TracesTrendDataSchema),
});

// Type exports for traces trend
export type TracesTrendQuery = z.infer<typeof TracesTrendQuerySchema>;
export type TracesTrendData = z.infer<typeof TracesTrendDataSchema>;
export type TracesTrendResponse = z.infer<typeof TracesTrendResponseSchema>;
